import { api } from "./api/client";
import type { AIAsset, BoardItem, Workflow, WorkflowItem, WorkflowKept, WorkflowStep, WorkflowTrialRow } from "./api/types";
import { dateInputDay, dayInput, todayNo } from "./customers";
import { md } from "./boardStats";

/*
 * The 销售工作流's own arithmetic, shared by the pages that touch it: the workflow page (components/WorkflowEditor.tsx)
 * and a chat's 小爱这样想, which says what stood behind a reply (Transcript.tsx).
 */

export const newID = () => `w${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export function stepOf(w: Workflow, p: string, s: string): WorkflowStep {
  return w.products?.[p]?.steps?.[s] ?? {};
}

export function withStep(w: Workflow, p: string, s: string, st: WorkflowStep): Workflow {
  const products = { ...(w.products ?? {}) };
  const steps = { ...(products[p]?.steps ?? {}) };
  steps[s] = st;
  products[p] = { steps };
  return { products };
}

export const MAX_TEXT = 200;

/** A typed line of a colleague's, as the takeover names it: 「讲口味（打字）」 (api console/board_takeover.go). */
const typedAbout = (it: BoardItem) => it.kind === "文字" && it.label.endsWith("（打字）") && !it.label.startsWith("聊别的");

/** What a board row about something said becomes as a point to make: its subject and, as a guide, a colleague's words. */
export function pointOf(it: BoardItem): string {
  const label = it.label.replace(/（打字）$/, "");
  // The opening asks no 东马还是西马 since 10/15 (v297): the voice notes that did are still named for it.
  const base = label.startsWith("开场问") ? "先问之前用过吗" : label === "报价" ? "报价" : label;
  const text = it.quote ? `${base}：${it.quote}` : base;
  return [...text].slice(0, MAX_TEXT).join("");
}

/** Whether a board row can go into a step as it is: a library file, or what a voice note or a typed line was about. */
export function usable(it: BoardItem, assets: Map<string, AIAsset>): "file" | "say" | "library" | "" {
  if (it.key.startsWith("lib:") && assets.has(it.key.slice(4))) return "file";
  if (it.kind === "录音" && (it.label.startsWith("讲") || it.label.startsWith("开场问") || it.label === "报价" || it.label.startsWith("收单"))) return "say";
  if (it.kind === "文字" && (it.label === "打字报价" || typedAbout(it))) return "say";
  if ((it.kind === "图" || it.kind === "影片") && !it.lib && it.media_key) return "library";
  return "";
}

/** An item made from a board row. */
export function itemOf(it: BoardItem): WorkflowItem {
  if (it.key.startsWith("lib:")) return { id: newID(), kind: "file", asset: it.key.slice(4), source: it.key };
  if (it.kind === "文字" && it.label === "打字报价") return { id: newID(), kind: "say", text: "报价直接打字，写清楚几罐多少钱", source: it.key };
  if (it.kind === "文字") return { id: newID(), kind: "say", text: pointOf(it), source: it.key };
  return { id: newID(), kind: "say", text: pointOf(it), voice: true, source: it.key };
}

/* ---- trials ---- */

/** A trial is judged by sales, but at the two after-sale steps, whose goal is a reply (owner, 2026-10-23: 「我要的是
 *  业绩提升 成交率提升」; 开场 too from 11/1 — by replies, 谷粮's read 78% against 86% while B bought twice A's share). */
export const trialByReply = (s: string) => s === "收到货" || s === "快喝完";

/** Both ways need this many customers before one is called ahead. */
export const TRIAL_MIN = 20;

/** The z of differs() (95% sure), and the one more that catches a gap of this size four times in five. */
const Z_SURE = 1.96;
const Z_CATCH = 0.84;

/**
 * About when the trial will tell, if each way keeps its rate and customers keep coming as they have since it began.
 * The owner, 11/1, on 「人数永远不够」: 「谷粮人多是因为有促销 排毒呢？…11月是代餐有会开始变多」 — whether a step
 * has customers enough follows the promotions, so the page says it from the counts rather than anyone deciding it.
 */
export function trialWhen(trial: WorkflowTrialRow, byReply: boolean, today: number): { text: string; tip?: string } {
  const { a, b } = trial;
  const k = (c: WorkflowTrialRow["a"]) => (byReply ? c.replied : c.bought);
  if (a.customers === 0 || b.customers === 0) return { text: "要两边都有客户，才算得出几号看得出" };
  if (k(a) + k(b) === 0) return { text: `两边都还没人${byReply ? "回话" : "买"}，还算不出几号看得出` };
  const pa = k(a) / a.customers;
  const pb = k(b) / b.customers;
  if (pa === pb) return { text: "两边一样，再多人也分不出谁好" };
  const need = Math.max(TRIAL_MIN, Math.ceil(((Z_SURE + Z_CATCH) ** 2 * (pa * (1 - pa) + pb * (1 - pb))) / (pa - pb) ** 2));
  const have = Math.min(a.customers, b.customers);
  const perDay = (a.customers + b.customers) / 2 / Math.max(1, today - dateInputDay(trial.since));
  const days = Math.ceil((need - have) / perDay);
  const each = need > 10000 ? "上万" : `约 ${need}`;
  const tip = `A ${Math.round(100 * pa)}% 对 B ${Math.round(100 * pb)}%：每边要${each} 人才分得出，现在少的那边 ${have} 人，一边一天约 ${perDay.toFixed(1)} 人`;
  if (days <= 0) return { text: "人差不多够了，再看几天", tip };
  if (days > 365) return { text: "两边差得少，照现在的人数要一年以上", tip };
  return { text: `照现在的人数，大约 ${md(dayInput(today + days))} 看得出`, tip };
}

/** The outcome a trial's 留下 B / 不试了 keeps on its step, from the live counts when the trial was live. */
export function keptOf(way: "A" | "B", step: string, trial: WorkflowTrialRow): WorkflowKept {
  const byReply = trialByReply(step);
  const hit = (c: WorkflowTrialRow["a"]) => (byReply ? c.replied : c.bought);
  return {
    way,
    since: trial.since,
    until: dayInput(todayNo()),
    by: byReply ? "replied" : "bought",
    a: { customers: trial.a.customers, hit: hit(trial.a) },
    b: { customers: trial.b.customers, hit: hit(trial.b) },
  };
}

/** A kept trial in a line: 「10/26 留下 B：B 21% 对 A 3%（买了）」. */
export function keptLine(k: WorkflowKept): string {
  const pct = (t: { customers: number; hit: number }) => (t.customers > 0 ? Math.round((100 * t.hit) / t.customers) : 0);
  const word = k.by === "replied" ? "回话" : "买了";
  const [won, lost] = k.way === "B" ? (["B", "A"] as const) : (["A", "B"] as const);
  const n = (w: "A" | "B") => (w === "A" ? k.a : k.b);
  return `${md(k.until)} ${k.way === "B" ? "留下 B" : "不试了，照 A"}：${won} ${pct(n(won))}% 对 ${lost} ${pct(n(lost))}%（${word}，${won} ${n(won).customers} 人、${lost} ${n(lost).customers} 人）`;
}

/* ---- what stood behind a reply ---- */

const PRODUCT_WORD: Record<string, string> = {
  grain: "谷粮",
  detox: "排毒",
  sleep: "Zenite",
  meal: "代餐",
  merihers: "MERIHERS",
  msecret: "M-SECRET",
  zentos: "ZENTOS",
};

/** A step as people say it: 停在报价 for 追回·报价. */
export const stepWord = (s: string) => (s.startsWith("追回·") ? `停在${s.slice(3)}` : s);

/** The live trials, read once every few minutes for every chat that shows them. */
let trialsRead: { at: number; rows: Promise<WorkflowTrialRow[]> } | null = null;
export function liveTrials(): Promise<WorkflowTrialRow[]> {
  if (!trialsRead || Date.now() - trialsRead.at > 5 * 60_000) {
    trialsRead = { at: Date.now(), rows: api.workflowTrial().then((r) => r.rows ?? [], () => []) };
  }
  return trialsRead.rows;
}

/**
 * What stood behind one of 小爱's replies, in words: the step and the way it took, and what that step stands on now — a
 * trial under way, the trial that decided it, or nobody's numbers. Owner, 2026-11-02: 「用来做展示让人看为什么小爱会那么
 * 回是有数据依据的不是凭感觉？」 — so a step nobody tried says so.
 */
export function replyBasis(
  row: { product?: string; step?: string; variant?: string; case?: string; kept?: WorkflowKept },
  trials: WorkflowTrialRow[],
  today: number,
): { took: string; basis: string; mark: "trial" | "kept" | "none"; tip?: string } {
  if (!row.step) return { took: "没走工作流", basis: "客户没说是哪一款，照说话规矩回的，没有数据", mark: "none" };
  const p = row.product ?? "";
  const took = `工作流 · ${PRODUCT_WORD[p] ?? p} · ${stepWord(row.step)}${row.variant ? ` · ${row.variant}` : ""}${row.case ? ` · ${row.case}` : ""}`;
  const trial = trials.find((t) => t.product === p && t.step === row.step);
  if (trial) {
    const byReply = trialByReply(row.step);
    const k = (c: WorkflowTrialRow["a"]) => (byReply ? c.replied : c.bought);
    const pct = (c: WorkflowTrialRow["a"]) => (c.customers > 0 ? Math.round((100 * k(c)) / c.customers) : 0);
    const when = trialWhen(trial, byReply, today);
    return {
      took,
      basis: `这一格在试：A ${pct(trial.a)}% 对 B ${pct(trial.b)}%（${byReply ? "回话" : "买了"}，A ${trial.a.customers} 人、B ${trial.b.customers} 人）· ${when.text}`,
      mark: "trial",
      tip: when.tip,
    };
  }
  if (row.kept) return { took, basis: `这一格试过：${keptLine(row.kept)}`, mark: "kept" };
  return { took, basis: "这一格没试过，是排的人定的，没有数据", mark: "none" };
}
